import {
    AstNodeType,
    FieldTypeCategory,
    isParameterTyped,
    isSeriesTyped,
    renderSlidingWindow,
    renderTimeframe,
} from '@thinkalpha/language-services';
import type { AllowedValueType, FunctionOverloadDef, KnownAstInfo, ParameterDef } from '@thinkalpha/language-services';
import {
    getAllowedArgumentValue,
    isAllowedArgumentAvailableForTarget,
} from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type {
    ConcreteIndicator,
    IndicatorImport,
    IndicatorParameter,
    IndicatorRef,
} from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import { humanizePointInTime } from '@thinkalpha/ui-core/util/timeframe';
import {
    allowedValuesForTarget,
    getAllowedArgumentValueViewModel,
    isEnumeratedOptionAllowedArgument,
    isSlidingWindowAllowedArgument,
    isTimeframeAllowedArgument,
} from '@thinkalpha/ui-types/contracts/dictionary-view-model';
import type {
    AllowedArgumentViewModel,
    FormulaTargetViewModel,
    IndicatorImportViewModel,
    IndicatorImportRefViewModel,
    IndicatorParameterViewModel,
} from '@thinkalpha/ui-types/contracts/dictionary-view-model';
import { isPointInTime, isTimeframe } from '@thinkalpha/ui-types/contracts/timeframe';
import { isIndicatorImport, isIndicatorViewModelImport } from 'src/lib/dictionaryModel';
import type {
    DictionaryFunctionDef,
    DictionaryViewModelFunctionDef,
    IndicatorViewModelOrImportViewModel,
} from 'src/lib/dictionaryModel';

export function determineDataType(value: AllowedValueType): FieldTypeCategory {
    switch (typeof value) {
        case 'string':
            return FieldTypeCategory.String;
        case 'number':
            return FieldTypeCategory.Double;
        case 'object': {
            // if (value === null) return '';
            if (isTimeframe(value)) return FieldTypeCategory.Timeframe;
            if (isPointInTime(value)) return FieldTypeCategory.PointInTime;
            return FieldTypeCategory.Duration;
        }
    }

    throw new Error(`Cannot determine data type of: ${value}`);
}

export const isFullIndicatorImport = (
    ref: IndicatorImportViewModel | IndicatorImportRefViewModel,
): ref is IndicatorImportViewModel => {
    return (ref as IndicatorImportViewModel).formula !== undefined;
};

export const filterIndicatorImportsOrRefs = (
    refs: (IndicatorImportViewModel | IndicatorImportRefViewModel)[],
): IndicatorImportViewModel[] => {
    return refs.filter((x) => isFullIndicatorImport(x)) as IndicatorImportViewModel[];
};

export interface FunctionDefMappingOptions {
    target?: FormulaTargetViewModel;
}

/**
 * @deprecated
 */
export function mapIndicatorViewModelToDictionaryFunctionDef(
    imp: IndicatorViewModelOrImportViewModel,
    options?: FunctionDefMappingOptions,
): DictionaryViewModelFunctionDef {
    const overloads: FunctionOverloadDef[] = [];
    if (
        !isSeriesTyped(imp.dataType) &&
        !isParameterTyped(imp.dataType) &&
        imp.params?.length === 1 &&
        imp.params?.every((x) => !isSeriesTyped(x.dataType))
    ) {
        overloads.push({
            params:
                imp.params?.map((x) =>
                    mapIndicatorViewModelParamToFunctionDefParam({ ...x, dataType: { series: x.dataType } }, options),
                ) ?? [],
            returnType: { series: imp.dataType },
            outputUnit: imp.unit ?? undefined,
        });
    }
    // this overload must come second, because of automatic downcasting
    overloads.push({
        params: imp.params?.map((x) => mapIndicatorViewModelParamToFunctionDefParam(x, options)) ?? [],
        returnType: imp.dataType,
        outputUnit: imp.unit ?? undefined,
    });

    return {
        id: imp.id,
        indicator: imp,
        overloads,
        symbol: isIndicatorViewModelImport(imp) ? imp.alias : imp.symbol,
        alias: isIndicatorViewModelImport(imp) ? imp.alias : undefined,
    };
}

export function mapIndicatorToDictionaryFunctionDef(
    imp: ConcreteIndicator | IndicatorImport,
    options?: FunctionDefMappingOptions,
): DictionaryFunctionDef {
    const overloads: FunctionOverloadDef[] = [];
    if (
        !isSeriesTyped(imp.dataType) &&
        !isParameterTyped(imp.dataType) &&
        imp.params?.length === 1 &&
        imp.params?.every((x) => !isSeriesTyped(x.dataType))
    ) {
        overloads.push({
            params:
                imp.params?.map((x) =>
                    mapIndicatorParamToFunctionDefParam({ ...x, dataType: { series: x.dataType } }, options),
                ) ?? [],
            returnType: { series: imp.dataType },
            outputUnit: imp.unit ?? undefined,
        });
    }
    // this overload must come second, because of automatic downcasting
    overloads.push({
        params: imp.params?.map((x) => mapIndicatorParamToFunctionDefParam(x, options)) ?? [],
        returnType: imp.dataType,
        outputUnit: imp.unit ?? undefined,
    });

    return {
        id: imp.id,
        indicator: imp,
        overloads,
        symbol: isIndicatorImport(imp) ? imp.alias : imp.symbol,
        alias: isIndicatorImport(imp) ? imp.alias : undefined,
    };
}

/**
 * @deprecated
 */
export function mapIndicatorViewModelParamToFunctionDefParam(
    param: IndicatorParameterViewModel,
    options?: FunctionDefMappingOptions,
): ParameterDef {
    return {
        dataType: param.dataType,
        allowedValues: allowedValuesForTarget(param.options?.values, options?.target)?.map((opt): KnownAstInfo => {
            const value = getAllowedArgumentValueViewModel(opt);
            return {
                type: AstNodeType.const,
                value,
                dataType: determineDataType(value),
            };
        }),
        unit: param.unit ?? undefined,
        name: param.name,
        optional: param.optional || param.defaultValue !== undefined,
    };
}

export function mapIndicatorParamToFunctionDefParam(
    param: IndicatorParameter,
    options?: FunctionDefMappingOptions,
): ParameterDef {
    const target = options?.target;
    const values =
        target !== undefined
            ? param.options?.values?.filter((arg) => isAllowedArgumentAvailableForTarget(arg, target))
            : param.options?.values;
    return {
        dataType: param.dataType,
        allowedValues: values?.map((opt): KnownAstInfo => {
            const value = getAllowedArgumentValue(opt);
            return {
                type: AstNodeType.const,
                value,
                dataType: determineDataType(value),
            };
        }),
        unit: param.unit ?? undefined,
        name: param.name,
        optional: param.optional || param.defaultValue !== undefined,
    };
}

// export const mapIndicatorToDictionaryFunctionDef = (x: IndicatorOrImport): DictionaryFunctionDef => ({
//     id: x.id,
//     indicator: x,
//     params: x.params.map(
//         (param): ParameterDef => ({
//             dataType: param.dataType,
//             name: param.name,
//             optional: param.optional || param.defaultValue !== undefined,
//             // todo: these next 2 properties will break if the default value of a param is an indicator ref or expression
//             allowedValues: param.options?.values?.map(
//                 (opt): KnownAstInfo => ({
//                     type: AstNodeType.const,
//                     value: isAllowedArgumentEnumerated(opt) ? opt.input : (opt as any),
//                     dataType: determineDataType(isAllowedArgumentEnumerated(opt) ? opt.input : (opt as any)),
//                 }),
//             ),
//         }),
//     ),
//     returnType: x.dataType,
//     symbol: isIndicatorImport(x) ? x.alias : x.symbol,
//     alias: isIndicatorImport(x) ? x.alias : undefined,
//     outputUnit: x.unit ?? undefined,
// });

export function parseAllowedArgument(argument: AllowedArgumentViewModel): string {
    if (isEnumeratedOptionAllowedArgument(argument)) return `${argument.input}`;
    if (isSlidingWindowAllowedArgument(argument)) return renderSlidingWindow(argument.value);
    if (isTimeframeAllowedArgument(argument)) return renderTimeframe(argument.value);
    if (isPointInTime(argument.value)) return humanizePointInTime(argument.value);
    return String(argument.value);
}

/**
 * The server often gives us back the full indicator, but when we give this back to the server,
 * it thinks we're giving it a bespoke indicator. Instead, we just want the id and version with
 * no additional properties.
 * @param indicatorRef The indicator, indicator ref, or anything else shaped like an indicator ref
 * @returns A bare indicator ref with no superfluous properties
 */
export function justTheIndicatorRef(indicatorRef: IndicatorRef): IndicatorRef {
    //
    return {
        id: indicatorRef.id,
    };
}
